/**
 * CampaignVisibilitySettings Component
 * Settings panel for controlling how events from other campaigns appear on the calendar.
 * Users can set per-campaign visibility to Show, Show Busy, or Hide.
 */

import { Eye, EyeOff, Loader2 } from 'lucide-react';
import type { CampaignCalendarVisibility } from '@signalvote/shared';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  useCampaignVisibility,
  useUpdateCampaignVisibility,
} from '@/hooks/queries/useCalendar';
import { CalendarSettingsReadNotice } from './CalendarSettingsReadNotice';

const VISIBILITY_OPTIONS: Array<{ value: CampaignCalendarVisibility; label: string; description: string }> = [
  { value: 'Show', label: 'Show', description: 'Full event details' },
  { value: 'ShowBusy', label: 'Show Busy', description: 'Time blocks only' },
  { value: 'Hide', label: 'Hide', description: 'Not visible' },
];

export function CampaignVisibilitySettings() {
  const read = useCampaignVisibility(undefined);
  const updateVisibility = useUpdateCampaignVisibility();
  const campaigns = read.status === 'ready' ? read.data : undefined;

  // Nothing to set without another campaign; idle means no campaign is selected yet.
  if (read.status === 'idle' || campaigns?.length === 0) {
    return null;
  }

  const handleChange = (campaignId: string, visibility: CampaignCalendarVisibility) => {
    updateVisibility.mutate({ campaignId, visibility });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Eye className="h-4 w-4" />
          Cross-Campaign Events
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Control how events from your other campaigns appear on this calendar.
        </p>
      </CardHeader>
      <CardContent>
        {read.status === 'failed' || read.status === 'offline' ? (
          <CalendarSettingsReadNotice read={read} subject="your other campaigns" />
        ) : !campaigns ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((campaign) => (
              <div
                key={campaign.campaignId}
                className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {campaign.visibility === 'Hide' ? (
                    <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium truncate">
                    {campaign.campaignName}
                  </span>
                </div>
                <select
                  value={campaign.visibility}
                  onChange={(e) => handleChange(campaign.campaignId, e.target.value as CampaignCalendarVisibility)}
                  disabled={updateVisibility.isPending}
                  className="text-sm border border-input rounded-md px-2 py-1 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
